import {useSelector} from 'react-redux';
import {useMemo} from 'react';
import {roomRunFileSelector, roomSelector} from '../store/selectors/roomRunFile.selectors';
import {
    currentSlideIndexSelector,
    lastShowedSlideSelector,
    loadedPresentationSelector,
    sequenceQueueSelector,
    visibleSlideSelector,
} from '../store/selectors/presentation.selectors';
import {runFileElementInteractionsSelector} from '../store/selectors/runfile.selectors';
import {checkShouldBlock, checkIfBlockedInRoom} from '../util/blockingRules';
import {IRunFile} from '../incase-domain/proptypes/runfile';

export function useSlideSelectable(index: number): {
    isSelectable: boolean;
    blockedTooltipKey: 'playerPage.nextSlideBlockedByRoomOwner' | 'playerPage.nextSlideBlocked';
    isActive: boolean;
    isNext: boolean;
} {
    const loadedPresentation = useSelector(loadedPresentationSelector);
    const slides = useSelector(visibleSlideSelector);
    const currentSlideIndex = useSelector(currentSlideIndexSelector);
    const lastShowedIndex = useSelector(lastShowedSlideSelector);
    const elementInteractions = useSelector(runFileElementInteractionsSelector);
    const room = useSelector(roomSelector);
    const roomRunFile = useSelector(roomRunFileSelector);
    const sequenceQueue = useSelector(sequenceQueueSelector);

    const isActive = index === currentSlideIndex;
    const isNext = index === currentSlideIndex + 1;

    const blockedTooltipKey = useMemo(() => {
        const isRoom = !!room.roomId && !!roomRunFile._id;
        const blockedByRoom = isRoom && checkIfBlockedInRoom(room, roomRunFile, index);
        return blockedByRoom ? 'playerPage.nextSlideBlockedByRoomOwner' : 'playerPage.nextSlideBlocked';
    }, [room, roomRunFile, index]);

    const isSelectable = useMemo(() => {
        if (sequenceQueue.length > 0) return true;

        const prevSlide = (slides || [])[index - 1];
        const appliedRules = loadedPresentation?.appliedRules;

        if (!prevSlide) return false;

        const runFileForBlocking = {elementInteractions} as IRunFile;
        return (
            index === 0 ||
            !checkShouldBlock(appliedRules, prevSlide, runFileForBlocking, room, roomRunFile, index, lastShowedIndex, currentSlideIndex)
        );
    }, [
        index,
        currentSlideIndex,
        sequenceQueue.length,
        loadedPresentation?.appliedRules,
        slides,
        elementInteractions,
        room,
        roomRunFile,
        lastShowedIndex,
    ]);

    return {isSelectable, blockedTooltipKey, isActive, isNext};
}
